import React, { useState, useEffect } from 'react';
import { 
  Database, 
  ArrowRight, 
  CheckCircle2, 
  AlertTriangle, 
  Clock, 
  RefreshCw,
  Zap,
  FileText,
  Shield,
  BarChart3,
  Upload,
  Users,
  Leaf,
  Link2,
  TrendingUp,
  AlertCircle,
  ChevronRight,
  Play,
  Eye
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Data Flow Node Component
const FlowNode = ({ icon: Icon, label, status, count, onClick, isActive }) => {
  const statusColors = {
    synced: 'bg-emerald-100 border-emerald-300 text-emerald-700',
    pending: 'bg-amber-100 border-amber-300 text-amber-700',
    no_data: 'bg-slate-100 border-slate-300 text-slate-500',
    has_suggestions: 'bg-purple-100 border-purple-300 text-purple-700',
    error: 'bg-red-100 border-red-300 text-red-700'
  };
  
  const statusIcons = {
    synced: CheckCircle2,
    pending: Clock,
    no_data: AlertCircle,
    has_suggestions: Zap,
    error: AlertTriangle
  };
  
  const StatusIcon = statusIcons[status] || Clock;
  
  return (
    <button
      onClick={onClick}
      className={`relative p-4 rounded-xl border-2 transition-all hover:shadow-lg ${
        isActive ? 'ring-2 ring-emerald-500 ring-offset-2' : ''
      } ${statusColors[status] || statusColors.no_data}`}
      data-testid={`flow-node-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex flex-col items-center gap-2">
        <Icon size={28} />
        <span className="text-xs font-semibold text-center">{label}</span>
        {count !== undefined && (
          <span className="text-lg font-bold">{count}</span>
        )}
      </div>
      <div className="absolute -top-2 -right-2">
        <StatusIcon size={16} className="bg-white rounded-full" />
      </div>
    </button>
  );
};

// Flow Arrow Component
const FlowArrow = ({ label, status, count }) => {
  const isActive = status === 'synced' || status === 'has_suggestions';
  return (
    <div className="flex flex-col items-center justify-center px-2">
      <div className={`flex items-center gap-1 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`}>
        <div className={`h-0.5 w-8 ${isActive ? 'bg-emerald-400' : 'bg-slate-300'}`} />
        <ArrowRight size={16} />
      </div>
      {label && (
        <span className={`text-[10px] mt-1 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`}>
          {label}
        </span>
      )}
      {count !== undefined && count > 0 && (
        <span className="text-xs font-bold text-emerald-600">{count}</span>
      )}
    </div>
  );
};

// KPI Card Component
const KPICard = ({ icon: Icon, label, value, unit, trend, color = 'emerald' }) => {
  const colors = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700'
  };
  
  return (
    <div className={`p-4 rounded-xl border ${colors[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon size={20} />
        {trend && (
          <span className={`text-xs flex items-center gap-1 ${trend > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            <TrendingUp size={12} className={trend < 0 ? 'rotate-180' : ''} />
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs opacity-75">{label} {unit && `(${unit})`}</p>
    </div>
  );
};

// Module Detail Panel
const ModuleDetailPanel = ({ module, onClose, onSync }) => {
  if (!module) return null;
  
  const moduleInfo = {
    emissions: {
      title: 'Emissions Data',
      description: 'Scope 1, 2, 3 emissions from your uploads',
      fields: ['Scope 1', 'Scope 2 (Location)', 'Scope 2 (Market)', 'Scope 3 Total', '15 Scope 3 Categories'],
      nextStep: 'Auto-populates ESRS E1 data points'
    },
    esrs: {
      title: 'ESRS Data Points',
      description: '157 disclosure data points across 12 standards',
      fields: ['E1-E5 Environmental', 'S1-S4 Social', 'G1 Governance'],
      nextStep: 'Generates XBRL filing elements'
    },
    xbrl: {
      title: 'XBRL Export',
      description: 'Machine-readable regulatory filings',
      fields: ['ESRS Taxonomy', 'SEC Climate', 'GRI Standards'],
      nextStep: 'Ready for regulatory submission'
    },
    csddd: {
      title: 'CSDDD Due Diligence',
      description: 'EU Corporate Sustainability Due Diligence',
      fields: ['Risk Identification', 'Remediation Plans', 'Article 14 Reporting'],
      nextStep: 'Due diligence statement publication'
    },
    stakeholder: {
      title: 'Stakeholder Engagement',
      description: 'Stakeholder groups and engagement channels',
      fields: ['Engagement Channels', 'Concerns Raised', 'Grievance Mechanism'],
      nextStep: 'CSDDD grievance compliance'
    }
  };
  
  const info = moduleInfo[module] || { title: module, description: '', fields: [] };
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-slate-900">{info.title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">×</button>
      </div>
      <p className="text-sm text-slate-600 mb-4">{info.description}</p>
      <div className="space-y-2 mb-4">
        <p className="text-xs font-semibold text-slate-700">Data Fields:</p>
        {info.fields.map((field, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
            <CheckCircle2 size={14} className="text-emerald-500" />
            {field}
          </div>
        ))}
      </div>
      {info.nextStep && (
        <div className="bg-emerald-50 rounded-lg p-3 mb-4">
          <p className="text-xs text-emerald-700">
            <strong>Next:</strong> {info.nextStep}
          </p>
        </div>
      )}
      <button
        onClick={() => onSync(module)}
        className="w-full py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center justify-center gap-2"
      >
        <RefreshCw size={16} />
        Sync Now
      </button>
    </div>
  );
};

// Main Component
const DataIntelligenceHub = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [recentSyncs, setRecentSyncs] = useState([]);

  useEffect(() => {
    loadSyncStatus();
  }, []);

  const loadSyncStatus = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    const [statusData, historyData] = await Promise.all([
      apiFetch(`${API}/sync/status`),
      apiFetch(`${API}/sync/history?limit=5`)
    ]);

    if (statusData?._error || historyData?._error) {
      console.error('Failed to load sync status:', statusData?.message || historyData?.message);
    } else {
      setSyncStatus(statusData);
      setRecentSyncs(historyData.events || []);
    }
    setLoading(false);
  };

  const triggerFullSync = async () => {
    setSyncing(true);
    const data = await apiFetch(`${API}/sync/trigger`, { method: 'POST', body: {} });
    if (data?._error) {
      console.error('Sync failed:', data.message);
    } else {
      await loadSyncStatus();
    }
    setSyncing(false);
  };

  const triggerModuleSync = async (module) => {
    setSyncing(true);
    const endpoints = {
      emissions: '/sync/emissions-to-esrs',
      esrs: '/xbrl/generate-from-esrs',
      stakeholder: '/sync/stakeholder-to-csddd'
    };

    let failed = false;
    if (endpoints[module]) {
      const data = await apiFetch(`${API}${endpoints[module]}`, { method: 'POST', body: {} });
      if (data?._error) {
        console.error('Module sync failed:', data.message);
        failed = true;
      }
    }
    if (!failed) {
      await loadSyncStatus();
    }
    setSyncing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const modules = syncStatus?.modules || {};

  return (
    <div className="max-w-7xl mx-auto" data-testid="data-intelligence-hub">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <Database className="text-emerald-600" size={28} />
              Data Intelligence Hub
            </h1>
            <p className="text-slate-600 mt-1">Cross-module data flow visualization and sync control center</p>
          </div>
          <button
            onClick={triggerFullSync}
            disabled={syncing}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
            data-testid="full-sync-btn"
          >
            <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing All...' : 'Sync All Modules'}
          </button>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPICard
          icon={Database}
          label="Auto-Populated"
          value={modules.emissions_to_esrs?.auto_populated_count || 0}
          unit="ESRS fields"
          color="emerald"
        />
        <KPICard
          icon={FileText}
          label="XBRL Elements"
          value={modules.esrs_to_xbrl?.xbrl_elements_count || 0}
          unit="ready"
          color="blue"
        />
        <KPICard
          icon={AlertTriangle}
          label="Risk Suggestions"
          value={modules.supply_chain_to_csddd?.pending_suggestions || 0}
          unit="pending"
          color="purple"
        />
        <KPICard
          icon={CheckCircle2}
          label="Modules Synced"
          value={Object.values(modules).filter(m => m.status === 'synced').length}
          unit="of 4"
          color="amber"
        />
      </div>

      {/* Main Data Flow Visualization */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Link2 className="text-emerald-600" size={20} />
          Cross-Module Data Flow
        </h2>
        
        {/* Primary Flow: Emissions → ESRS → XBRL */}
        <div className="mb-8">
          <p className="text-xs text-slate-500 uppercase font-semibold mb-3">Environmental & Financial Reporting</p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <FlowNode
              icon={Upload}
              label="Data Upload"
              status={modules.emissions_to_esrs?.has_source_data ? 'synced' : 'no_data'}
              onClick={() => setSelectedModule('emissions')}
              isActive={selectedModule === 'emissions'}
            />
            <FlowArrow 
              label="auto-populate" 
              status={modules.emissions_to_esrs?.status}
              count={modules.emissions_to_esrs?.auto_populated_count}
            />
            <FlowNode
              icon={Database}
              label="ESRS Data"
              status={modules.emissions_to_esrs?.status}
              count={modules.emissions_to_esrs?.auto_populated_count}
              onClick={() => setSelectedModule('esrs')}
              isActive={selectedModule === 'esrs'}
            />
            <FlowArrow 
              label="generate" 
              status={modules.esrs_to_xbrl?.status}
              count={modules.esrs_to_xbrl?.xbrl_elements_count}
            />
            <FlowNode
              icon={FileText}
              label="XBRL Filing"
              status={modules.esrs_to_xbrl?.status}
              count={modules.esrs_to_xbrl?.xbrl_elements_count}
              onClick={() => setSelectedModule('xbrl')}
              isActive={selectedModule === 'xbrl'}
            />
            <FlowArrow label="submit" status="pending" />
            <FlowNode
              icon={CheckCircle2}
              label="Regulatory"
              status="pending"
              onClick={() => {}}
            />
          </div>
        </div>

        {/* Secondary Flow: Supply Chain → CSDDD */}
        <div className="mb-8">
          <p className="text-xs text-slate-500 uppercase font-semibold mb-3">Supply Chain & Due Diligence</p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <FlowNode
              icon={Users}
              label="Stakeholders"
              status={modules.stakeholder_to_csddd?.has_source_data ? 'synced' : 'no_data'}
              onClick={() => setSelectedModule('stakeholder')}
              isActive={selectedModule === 'stakeholder'}
            />
            <FlowArrow 
              label="grievance" 
              status={modules.stakeholder_to_csddd?.status}
            />
            <FlowNode
              icon={Shield}
              label="CSDDD"
              status={modules.stakeholder_to_csddd?.grievance_synced ? 'synced' : 'pending'}
              onClick={() => setSelectedModule('csddd')}
              isActive={selectedModule === 'csddd'}
            />
            <FlowArrow 
              label="suggestions" 
              status={modules.supply_chain_to_csddd?.status}
              count={modules.supply_chain_to_csddd?.pending_suggestions}
            />
            <FlowNode
              icon={AlertTriangle}
              label="Risk Alerts"
              status={modules.supply_chain_to_csddd?.status}
              count={modules.supply_chain_to_csddd?.pending_suggestions}
              onClick={() => {}}
            />
          </div>
        </div>

        {/* Module Detail Panel */}
        {selectedModule && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <ModuleDetailPanel
              module={selectedModule}
              onClose={() => setSelectedModule(null)}
              onSync={triggerModuleSync}
            />
          </div>
        )}
      </div>

      {/* Sync Status Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Module Status */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="text-emerald-600" size={18} />
            Module Sync Status
          </h3>
          <div className="space-y-3">
            {Object.entries(modules).map(([key, mod]) => (
              <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${
                    mod.status === 'synced' ? 'bg-emerald-500' :
                    mod.status === 'has_suggestions' ? 'bg-purple-500' :
                    mod.status === 'pending' ? 'bg-amber-500' : 'bg-slate-300'
                  }`} />
                  <span className="text-sm font-medium text-slate-700">
                    {key.replace(/_/g, ' → ').replace(/to/g, '→')}
                  </span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  mod.status === 'synced' ? 'bg-emerald-100 text-emerald-700' :
                  mod.status === 'has_suggestions' ? 'bg-purple-100 text-purple-700' :
                  mod.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                }`}>
                  {mod.status?.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Sync Activity */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Clock className="text-emerald-600" size={18} />
            Recent Sync Activity
          </h3>
          {recentSyncs.length > 0 ? (
            <div className="space-y-3">
              {recentSyncs.map((sync, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <RefreshCw size={14} className="text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">
                        {sync.event_type?.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-slate-500">
                        {sync.changes_count} changes
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400">
                    {new Date(sync.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-4">No recent sync activity</p>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-5">
        <h3 className="font-semibold text-emerald-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <a
            href="/data-upload"
            className="p-3 bg-white rounded-lg border border-emerald-200 hover:shadow-md transition-shadow flex items-center gap-2 text-sm font-medium text-emerald-700"
          >
            <Upload size={16} />
            Upload Data
          </a>
          <a
            href="/esrs-data"
            className="p-3 bg-white rounded-lg border border-emerald-200 hover:shadow-md transition-shadow flex items-center gap-2 text-sm font-medium text-emerald-700"
          >
            <Database size={16} />
            ESRS Data Points
          </a>
          <a
            href="/xbrl-export"
            className="p-3 bg-white rounded-lg border border-emerald-200 hover:shadow-md transition-shadow flex items-center gap-2 text-sm font-medium text-emerald-700"
          >
            <FileText size={16} />
            XBRL Export
          </a>
          <a
            href="/csddd"
            className="p-3 bg-white rounded-lg border border-emerald-200 hover:shadow-md transition-shadow flex items-center gap-2 text-sm font-medium text-emerald-700"
          >
            <Shield size={16} />
            CSDDD Assessment
          </a>
        </div>
      </div>
    </div>
  );
};

export default DataIntelligenceHub;
